/**
 * The non-colour scales: space, radius, elevation, type, breakpoints, motion and touch
 * targets. They do not change with the theme or with the tenant — only colour does — so
 * they are frozen constants rather than anything resolved at runtime.
 *
 * Every value here is a named token. `docs/ux/DESIGN-SYSTEM.md` is the visual reference,
 * and D02–D04 are expected to write "space.lg", not "16".
 */

/**
 * Spacing, on a 4-unit grid.
 *
 * Four rather than the old app's eight (`spacing(q) = q * 8`), which could not express
 * the gap between a 13px caption and the line above it without doubling it. The names go
 * up to `4xl`; anything larger is a layout decision, not a token.
 */
export const space = {
  none: 0,
  hair: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  '2xl': 32,
  '3xl': 40,
  '4xl': 56,
} as const
export type SpaceToken = keyof typeof space

/**
 * Corner radii — the 2026-10 redesign's scale (`docs/ux/design/2026-10/`, "Space, radius,
 * elevation"): 4 for tags and small marks, 8 for controls, 12 for cards, lists and tables,
 * 16 for sheets, 20 for the one oversized surface, `full` for pills and avatars.
 *
 * Each name keeps the role it already had at its call sites — `sm` is a control, `md` a
 * card, `lg` a sheet — so the redesign moved the numbers, not the vocabulary.
 */
export const radius = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  full: 999,
} as const
export type RadiusToken = keyof typeof radius

/**
 * Border widths. `hairline` is the worksheet rule; `focus` is the focus ring, which is
 * deliberately thicker than any decorative border so it can never be mistaken for one.
 */
export const borderWidth = {
  none: 0,
  hairline: 1,
  thick: 2,
  focus: 2,
} as const
export type BorderWidthToken = keyof typeof borderWidth

/**
 * Four elevations, not the old app's twenty-five — the redesign's `raised`, `floating` and
 * `overlay` (`t4s.css`: `--shadow-raised`, `--shadow-float`, `--shadow-overlay`).
 *
 * Elevation means "this floats above the page and can be dismissed": raised is a card's
 * hairline lift, floating is a menu or a toast, overlay is a sheet. The values here are
 * the *geometry*; each theme supplies the shadow colour. `opacity` is the light scheme's,
 * `darkOpacity` the dark scheme's — in dark a card's shadow is dropped altogether (depth
 * is the surface's own lightness) and the two floating levels are drawn heavier, because a
 * shadow over a near-black canvas has to be strong to be seen at all.
 */
export const elevation = {
  flat: { offsetY: 0, blur: 0, spread: 0, opacity: 0, darkOpacity: 0 },
  raised: { offsetY: 1, blur: 2, spread: 0, opacity: 0.06, darkOpacity: 0 },
  floating: { offsetY: 6, blur: 16, spread: -4, opacity: 0.16, darkOpacity: 0.5 },
  overlay: { offsetY: 16, blur: 40, spread: -8, opacity: 0.28, darkOpacity: 0.7 },
} as const
export type ElevationToken = keyof typeof elevation

/**
 * Font families by role — **IBM Plex Sans**, loaded by the app (`docs/DECISIONS.md`,
 * "Tenant app redesign (2026-10)").
 *
 * `packages/ui` loads no fonts — that is the app's job, and a design system that reached
 * for the filesystem could not be rendered on the web, on a simulator and in a test the
 * same way. It names the faces the app registers (`fontFaces`) and the stack to fall back
 * on while they load, so the package renders correctly on its own and better once the app
 * has loaded the real files.
 *
 * `numeric` is a first-class role rather than a variant because the app's core loop is a
 * stopwatch: durations, counts and times must not jitter as the digits change, so they
 * are set with tabular figures wherever they appear.
 */
export const FONT_FALLBACK_STACK = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'

export const fontFamily = {
  sans: `"IBM Plex Sans", ${FONT_FALLBACK_STACK}`,
  numeric: `"IBM Plex Sans", ${FONT_FALLBACK_STACK}`,
  mono: 'ui-monospace, SFMono-Regular, "JetBrains Mono", Menlo, monospace',
} as const
export type FontFamilyToken = keyof typeof fontFamily

export const fontWeight = {
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
} as const
export type FontWeightToken = keyof typeof fontWeight
/** A weight as a style carries it: `'400'`, `'500'`, `'600'` or `'700'`. */
export type FontWeightValue = (typeof fontWeight)[FontWeightToken]

/**
 * The registered family name of each IBM Plex Sans weight — one *family per weight*, the
 * form every platform understands. A custom font on Android ignores `fontWeight` and picks
 * a file by family name alone, and `expo-font` on the web cannot declare a weight
 * descriptor, so a single "IBM Plex Sans" family with a numeric weight works on neither.
 * These are the names `@expo-google-fonts/ibm-plex-sans` exports its files under, and
 * `textStyleFor` writes the face for a role's weight into `fontFamily` instead of
 * `fontWeight` (setting both makes the web browser fake a bold on top of the semibold).
 * Latin and Cyrillic — the app ships `en`, `de`, `ru` and `uk` — are in each file.
 */
export const fontFaces = {
  '400': 'IBMPlexSans_400Regular',
  '500': 'IBMPlexSans_500Medium',
  '600': 'IBMPlexSans_600SemiBold',
  '700': 'IBMPlexSans_700Bold',
} as const satisfies Record<FontWeightValue, string>

export type TypeStyle = {
  readonly fontSize: number
  readonly lineHeight: number
  readonly fontWeight: (typeof fontWeight)[FontWeightToken]
  readonly letterSpacing: number
  readonly family: FontFamilyToken
  readonly uppercase?: true
  /** Tabular figures, so a changing digit never shifts the layout. */
  readonly tabular?: true
}

/**
 * The type scale.
 *
 * Body is 16, not the 13–14 the old app used, because the primary reader is a worker
 * holding a phone in a stairwell mid-shift. Density in the admin views comes from
 * spacing, never from dropping text below `caption`'s 13.
 *
 * `overline` is the structural device: a short uppercase label that names a section — the
 * room, the location, the date group. It carries meaning (what this block is), which is
 * why it is a type role rather than decoration.
 */
export const typeScale = {
  display: {
    fontSize: 34,
    lineHeight: 40,
    fontWeight: fontWeight.bold,
    letterSpacing: -0.34,
    family: 'sans',
  },
  title: {
    fontSize: 24,
    lineHeight: 30,
    fontWeight: fontWeight.bold,
    letterSpacing: -0.12,
    family: 'sans',
  },
  heading: {
    fontSize: 19,
    lineHeight: 25,
    fontWeight: fontWeight.semibold,
    letterSpacing: 0,
    family: 'sans',
  },
  subheading: {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: fontWeight.semibold,
    letterSpacing: 0,
    family: 'sans',
  },
  body: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: fontWeight.regular,
    letterSpacing: 0,
    family: 'sans',
  },
  bodyStrong: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: fontWeight.semibold,
    letterSpacing: 0,
    family: 'sans',
  },
  /** A field's label and a small button's word: 14/18 semibold. */
  label: {
    fontSize: 14,
    lineHeight: 18,
    fontWeight: fontWeight.semibold,
    letterSpacing: 0,
    family: 'sans',
  },
  callout: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: fontWeight.regular,
    letterSpacing: 0,
    family: 'sans',
  },
  caption: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: fontWeight.medium,
    letterSpacing: 0,
    family: 'sans',
  },
  /** The word inside a status badge or a count: 13/16 semibold. */
  badge: {
    fontSize: 13,
    lineHeight: 16,
    fontWeight: fontWeight.semibold,
    letterSpacing: 0,
    family: 'sans',
  },
  overline: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: fontWeight.bold,
    letterSpacing: 0.88,
    family: 'sans',
    uppercase: true,
  },
  timer: {
    fontSize: 44,
    lineHeight: 48,
    fontWeight: fontWeight.semibold,
    letterSpacing: -0.44,
    family: 'numeric',
    tabular: true,
  },
  metric: {
    fontSize: 15,
    lineHeight: 20,
    fontWeight: fontWeight.medium,
    letterSpacing: 0,
    family: 'numeric',
    tabular: true,
  },
} as const satisfies Record<string, TypeStyle>
export type TypeToken = keyof typeof typeScale

/**
 * Breakpoints, as minimum widths.
 *
 * `md` is the one that carries a product requirement: `docs/reference/FUNCTIONALITY.md`
 * says the two-pane calendar-and-list layouts stack below it and sit side by side from it
 * up, so it is set at 768 — the narrowest tablet — rather than at the old app's 740.
 */
export const breakpoints = {
  xs: 0,
  sm: 480,
  md: 768,
  lg: 1024,
  xl: 1280,
  xxl: 1536,
} as const
export type Breakpoint = keyof typeof breakpoints

/** Breakpoints in ascending order, which is the order `resolveBreakpoint` walks. */
export const BREAKPOINT_ORDER = [
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  'xxl',
] as const satisfies readonly Breakpoint[]

/**
 * Minimum interactive sizes, in density-independent pixels.
 *
 * `minimum` is WCAG 2.2 target size (2.5.8) and the iOS floor. `comfortable` is the
 * default for anything a worker taps while holding the phone one-handed with a trolley in
 * the other hand — the whole primary action set. `large` is for the two controls the
 * timer loop is built around, which are pressed under time pressure.
 */
export const touchTarget = {
  /**
   * The design's `sm` button and compact icon button (36). Below `minimum`, so every
   * pressable drawn at this height grows its touch area back with `hitSlopFor`.
   */
  compact: 36,
  minimum: 44,
  comfortable: 48,
  large: 56,
} as const
export type TouchTargetToken = keyof typeof touchTarget

/**
 * Motion. Three durations and two curves; anything longer than `slow` is a screen
 * transition, which belongs to the navigator rather than to a component.
 *
 * Every animated component in this package must be able to run at duration 0 — see
 * `ThemeProvider`'s `reduceMotion`, which mirrors the OS setting.
 */
export const motion = {
  duration: { instant: 0, fast: 120, normal: 180, slow: 260 },
  easing: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    decelerate: 'cubic-bezier(0, 0, 0, 1)',
  },
} as const

// There is deliberately no `DISABLED_OPACITY`. A disabled control is drawn in the
// disabled *colours* — `surfaceSunken` behind, `text.disabled` in front — and never
// dimmed with a blanket opacity over the finished control. Opacity composites the label
// along with the fill: at 0.55 the disabled label that the token tests hold at 3.4–4.1:1
// renders at under 2.1:1, which is the WCAG 1.4.3 exemption this system refuses to take
// (`docs/ux/DESIGN-SYSTEM.md` §3, and `colors.test.ts`, "disabled text is still legible").
